import Head from 'next/head';
import { useTranslation } from 'react-i18next';
import { useEffect, useState, ReactNode } from 'react';
import { getLocalizedUrlsFromPath } from 'lib/Utilities';
import Header, { HeaderContainerWidth } from 'components/Header';
import Footer from 'components/Footer';
import { colors, screen, fonts } from 'components/styles/variables';
// assets
import ogDefaultLogo from 'public/static/images/social_media/og_default_placeholder.png';

import AuthDialog from 'components/Auth/Dialog';
import Alerts from 'components/Alerts';
import { LOCK_SCREEN_CLASS_NAME } from 'lib/hooks/useLockedScreen';
import { useRouter } from 'next/router';
import config from 'lib/config';
import { LANGUAGE_TO_LANGUAGE_WITH_COUNTRY_MAP } from 'lib/i18next';
import WebPageStructuredData from './WebPageStructuredData';

export interface LayoutProps {
  alternateAndroidUrl?: string;
  indexed: boolean;
  title: string;
  description: string;
  subNav?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
  id?: string;
  includeAds?: boolean;
  headerContainerWidth?: HeaderContainerWidth;
  showHeader?: boolean;
}

function getRobotsContent(indexed: boolean): string {
  let robotsTag = 'NOINDEX';
  if (indexed) {
    robotsTag = 'INDEX';
  }
  return `${robotsTag}, FOLLOW`;
}

const Layout = ({
  alternateAndroidUrl,
  indexed,
  title,
  description,
  subNav,
  footer,
  children,
  id,
  includeAds = true,
  headerContainerWidth,
  showHeader = true,
}: LayoutProps): JSX.Element => {
  const { asPath } = useRouter();
  const { i18n } = useTranslation();
  const robotsContent = getRobotsContent(indexed);
  const urls = getLocalizedUrlsFromPath(asPath, config.host);

  const [scrollTop, setScrollTop] = useState(0);
  const [scrollUp, setScrollUp] = useState(false);

  useEffect(() => {
    const setScroll = (): void => {
      const current = document.querySelector('#vdp-scroll-container')?.scrollTop || 0;
      setScrollUp(scrollTop > current);
      setScrollTop(current);
    };
    document.querySelector('#vdp-scroll-container')?.addEventListener('scroll', setScroll);
    return (): void => {
      document.querySelector('#vdp-scroll-container')?.removeEventListener('scroll', setScroll);
    };
  }, [scrollTop]);

  const showOnScroll = scrollTop > 50 && scrollUp;

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta content="app-id=com.autolist.autolist" name="google-play-app" />
        <meta content="app-id=B013TNEL8Q" name="kindle-fire-app" />
        <meta content="app-id=1058636900" name="apple-itunes-app" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <link rel="alternate" hrefLang="x-default" href={`https://${urls.en}`} />
        <link
          rel="alternate"
          hrefLang={LANGUAGE_TO_LANGUAGE_WITH_COUNTRY_MAP.get('en')}
          href={`https://${urls.en}`}
        />
        <link
          rel="alternate"
          hrefLang={LANGUAGE_TO_LANGUAGE_WITH_COUNTRY_MAP.get('es')}
          href={`https://${urls.es}`}
        />
        {alternateAndroidUrl && <link href={alternateAndroidUrl} rel="alternate" />}
        <meta
          content="width=device-width, initial-scale=1.0, user-scalable=yes, minimum-scale=1.0, maximum-scale=2.0"
          name="viewport"
        />
        <meta content={description} name="description" />
        <meta content="1386838238233125" property="fb:app_id" />
        <meta content={title} property="twitter:title" />
        <meta content={description} property="twitter:description" />
        <meta name="ROBOTS" content={robotsContent} key="robots" />
        <meta content={`https://${config.host}${asPath}`} property="og:url" key="og:url" />
        <meta content="Autolist" property="og:site_name" key="og:site_name" />
        <meta content={i18n.language} property="og:locale" key="og:locale" />
        <meta content={title} property="og:title" key="og:title" />
        <meta content={ogDefaultLogo} property="og:image" key="og:image" />
        <meta content="website" property="og:type" key="og:type" />
        <meta content={description} property="og:description" key="og:description" />
        <link
          href="https://production-assets1.autolist.com/assets/apple-icon-57x57-64229a979fda0ea26ab7bb9b029378997744fda466f3c002c7755b0e4393931f.png"
          rel="apple-touch-icon"
          sizes="57x57"
        />
        <link
          href="https://production-assets2.autolist.com/assets/apple-icon-60x60-e5fe3cb221cf95e144a36483277b73f10f5b54d0a4944343027698f062f670fa.png"
          rel="apple-touch-icon"
          sizes="60x60"
        />
        <link
          href="https://production-assets0.autolist.com/assets/apple-icon-72x72-3a5b62d250e21f275a7014261e71da906cd01e905604c6ed25242409652901f7.png"
          rel="apple-touch-icon"
          sizes="72x72"
        />
        <link
          href="https://production-assets1.autolist.com/assets/apple-icon-76x76-d5568feb81a859e718f93baef5b68ff8aa00ac4c291221513f9b307168142088.png"
          rel="apple-touch-icon"
          sizes="76x76"
        />
        <link
          href="https://production-assets2.autolist.com/assets/apple-icon-114x114-e04772f340dacadbd18a5e2f85b682c486b911f8658f0d6d7294fb02285d8ca2.png"
          rel="apple-touch-icon"
          sizes="114x114"
        />
        <link
          href="https://production-assets2.autolist.com/assets/apple-icon-120x120-21bcaae867ee887e84dd85bed69c5242a69dd8710b65a5951e76af2acd020a23.png"
          rel="apple-touch-icon"
          sizes="120x120"
        />
        <link
          href="https://production-assets0.autolist.com/assets/apple-icon-144x144-497ab60279119e4c12ce548da7bc2c53a7d225df10b37d221aa1e093639296ca.png"
          rel="apple-touch-icon"
          sizes="144x144"
        />
        <link
          href="https://production-assets2.autolist.com/assets/apple-icon-152x152-b32eff1616b547a5b346927dee7a6a64034e68d9439feb7ad7d47b3b642c5eee.png"
          rel="apple-touch-icon"
          sizes="152x152"
        />
        <link
          href="https://production-assets0.autolist.com/assets/apple-icon-180x180-2e697e249bc91bf44e32aeb8b81878e8126b9ee9502268ea5f3abbc529751d24.png"
          rel="apple-touch-icon"
          sizes="180x180"
        />
        <link
          href="https://production-assets2.autolist.com/assets/android-icon-192x192-bd9b531617caf45b447fac222e140dad09c55aa725bf28fe44fa69063eee2094.png"
          rel="icon"
          sizes="192x192"
          type="image/png"
        />
        <link
          href="https://production-assets2.autolist.com/assets/favicon-32x32-aff0c87cf69872e1d52e714f6d22c1f7e65f1c6d20c28cd0f70d642a8b9e118c.png"
          rel="icon"
          sizes="32x32"
          type="image/png"
        />
        <link
          href="https://production-assets2.autolist.com/assets/favicon-96x96-e767a6da33d448eaa874ca2a001c4ccca9484da04e1ace5c6f5fee2ba4817793.png"
          rel="icon"
          sizes="96x96"
          type="image/png"
        />
        <link
          href="https://production-assets2.autolist.com/assets/favicon-16x16-6db5d1fe9c2d3af140700326eeb8d4008b0a4cbeacabb985cdead4ca4dc76e96.png"
          rel="icon"
          sizes="16x16"
          type="image/png"
        />
        <link
          href="https://production-assets0.autolist.com/assets/favicos-75592a017d24b979ebf9c64e391f1bb43abed696d2e7704c9f8d05c07e6c703c.json"
          rel="manifest"
        />
      </Head>
      <div id={id}>
        {showHeader && (
          <Header
            url={asPath}
            subNav={subNav}
            includeAds={includeAds}
            showOnScroll={showOnScroll}
            containerWidth={headerContainerWidth}
          />
        )}

        <main className="container main">{children}</main>
        {footer || <Footer />}
      </div>
      <Alerts />
      <AuthDialog />

      <style jsx>{`
        .main {
          width: 100%;
          display: flex;
          flex-direction: column;
          padding: 0;
        }

        @media (max-width: ${screen.smMin}) {
          .main {
            /* Bootstrap override */
            padding: 0;
          }
        }
      `}</style>
      <style jsx global>
        {`
          html {
            overflow-x: hidden;
          }

          body {
            font-family: ${fonts.default};
            font-size: 16px;
            font-weight: 100;
            padding-right: inherit;
          }

          a {
            color: ${colors.ALR1};
            font-size: 15px;
            font-weight: 400;
            font-family: ${fonts.default};
            outline: none;
          }

          a.large {
            font-size: 19px !important;
            line-height: 25px !important;
          }

          a:hover,
          a:focus,
          a:active {
            text-decoration: none;
            color: red;
          }

          button {
            outline: none;
          }

          .h1 {
            margin: 0;
            padding: 0;
            font-size: 43px;
            font-weight: bold;
            line-height: 40px;
          }

          .h2 {
            margin: 0;
            padding: 0;
            font-size: 33px;
            font-weight: bold;
            line-height: 25px;
          }

          .h3 {
            margin: 0;
            padding: 0;
            font-size: 24px;
            font-weight: bold;
            line-height: 22px;
          }

          .row.row--small-gutters > [class*='col-'] {
            padding-left: 15px !important;
            padding-right: 15px !important;
          }

          @media (max-width: ${screen.xsMax}) {
            .row.row--small-gutters-xs {
              margin-left: 0px !important;
              margin-right: 0px !important;
            }

            .row.row--small-gutters-xs > [class*='col-'] {
              padding-left: 15px !important;
              padding-right: 15px !important;
            }
          }

          @media (min-width: ${screen.smMin}) {
            .row.row--small-gutters-gtxs > [class*='col-'] {
              padding-left: 15px !important;
              padding-right: 15px !important;
            }
          }

          @media (max-width: ${screen.xsMax}) {
            .row.row--no-gutters-xs {
              margin-left: 0px !important;
              margin-right: 0px !important;
            }

            .row.row--no-gutters-xs > [class*='col-'] {
              padding-left: 0px !important;
              padding-right: 0px !important;
            }
          }

          .AL-button {
            user-select: none;
            text-align: center;
            text-decoration: none;
            display: inline-block;
            background-color: ${colors.ALR1};
            color: white;
            padding: 20px;
            font-size: 18px;
            font-weight: 600;
            cursor: pointer;
            border: 0;
            border-radius: 50px;
            -webkit-tap-highlight-color: rgba(250, 250, 250, 0.5);
            transition: 1s all ease;
          }

          .AL-button:active,
          .AL-button:hover,
          .AL-button:visited,
          .AL-button:focus {
            color: white;
          }

          .AL-button--medium {
            width: 80%;
          }

          .AL-link {
            display: inline;
            color: ${colors.ALR1};
            font-size: 15px;
            font-weight: 400;
            font-family: ${fonts.default};
            padding: 0;
            margin: 0;
            text-decoration: none;
            border: none;
            background-color: transparent;
          }

          /*
          Removes a container that adds undesireable white space in the footer,
          the container is injected by 3rd party ads :/
        */
          body > img[width='1'] {
            display: none;
          }

          @media (min-width: ${screen.smMax}) {
            html.${LOCK_SCREEN_CLASS_NAME} body {
              overflow: visible;
            }
          }

          html.${LOCK_SCREEN_CLASS_NAME}, body.${LOCK_SCREEN_CLASS_NAME} {
            height: 100%;
            width: 100%;
            overflow: hidden;
          }

          html.${LOCK_SCREEN_CLASS_NAME}.scrolling-carousel {
            overflow-y: visible;
            overflow-x: visible;
          }
        `}
      </style>
      <WebPageStructuredData
        {...{
          indexed,
          title,
          description,
          host: config.host,
          path: asPath,
          subNav,
          footer,
          children,
          id,
        }}
      />
    </>
  );
};

export default Layout;
